import _, {last, noop} from 'lodash';
import React, {ClipboardEventHandler, Component, createRef, ReactNode, RefObject} from 'react';
import styled from 'styled-components';
import {Key} from 'ts-key-enum';

import {globalWindow} from '../../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {containsFocus} from '../../../../../libs/shared/core/src/helpers/browser/domHelpers';
import {hasMetaKey} from '../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {
  FrontClickEvent,
  FrontKeyboardEvent,
  KeyboardEventHandler,
} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {AutosizeInput} from '../../../../../libs/shared/ui/src/components/inputs/autosizeInput';
import {Input, InputTypesEnum} from '../../../../../libs/shared/ui/src/components/inputs/input';
import {Anchor} from '../../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {composerDetailsTokenBoxStyle} from '../../../../../libs/shared/ui/src/styles/composerStyles';
import {buildIndexMap} from '../../../../../srcCommon/helpers/itemIndexHelpers';
import {getSelectedIdsOnClick, getSelectedIdsOnKey} from '../../../../helpers/selectionHelpers';

/*
 * Props.
 */

interface TokensEditorProps {
  shouldFocus?: boolean;
  className?: string;
  placeholder?: string;
  tokenIds: ReadonlyArray<string>;
  renderToken: (index: number, isSelected: boolean) => ReactNode;
  renderTokenForCopy?: (index: number) => string | undefined;

  onRemoveTokensClick: (tokenIndexes: ReadonlyArray<number>) => void;
  onEditTokenClick?: (tokenIndex: number) => void;

  text: string;
  onTextChange: (arg: string) => void;

  onFocus?: () => void;
  onBlur?: () => void;
  inputId?: string;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;

  /* Contain the absolutely positioned hidden input. */
  position: relative;
`;

// How this behaves:
// - The input should stay on the same line as a token if the text fits, otherwise it should flex-wrap. This is why we
//   need the autosizing input.
// - If the input has already flex-wrapped to its own line and the text still doesn't fit, it should flex-shrink and
//   have regular input scroll behavior. This is why we can't give it a hard width, and use flex-basis instead.
// - Regardless of whether the input is on its own line, it should flex-grow to fill remaining space.
const StyledAutosizeInput = styled(AutosizeInput)`
  /* flex-basis is set by component based on the size of its text */
  flex-grow: 1;
  flex-shrink: 1;
  min-width: 30px;

  ${composerDetailsTokenBoxStyle};

  /* Work around caret sometimes being invisible or clipped in Safari (PB-15472). */
  transform: translateZ(0);
`;

const StyledHiddenInput = styled.input`
  /* Don't let this affect content flow. */
  position: absolute;

  /* Make this small while maintaining the ability to copy from this input. */
  font-size: 1px;
  width: 1em;
  height: 1em;
  border-width: 0;
  padding: 0;

  /* Make it invisible. */
  background-color: transparent;
  text-indent: 100%;
  overflow: hidden;
`;

const ChromelessButtonWrapperDiv = styled.div`
  max-width: 100%;
`;

/*
 * Component.
 */

interface TokensEditorState {
  selectedTokenIds: ReadonlyArray<string>;
}

const fallbackInputAnchor: Anchor = createRef<HTMLElement>();

export class TokensEditor extends Component<TokensEditorProps, TokensEditorState> {
  constructor(props: TokensEditorProps) {
    super(props);

    this.wrapperRef = createRef<HTMLDivElement>();
    this.hiddenInputRef = createRef<HTMLInputElement>();
    this.inputRef = createRef<Input>();
    this.lastTokenRef = createRef<HTMLDivElement>();

    this.state = {selectedTokenIds: []};
  }

  private dragTokenIds: ReadonlyArray<string> = [];
  private readonly wrapperRef: RefObject<HTMLDivElement>;
  private readonly hiddenInputRef: RefObject<HTMLInputElement>;
  private readonly inputRef: RefObject<Input>;
  private readonly lastTokenRef: RefObject<HTMLDivElement>;
  private onBlurTimeout?: number;

  /** Returns token IDs for dragging in the order they were provided in the tokenIds prop. */
  public getDragTokenIndexes(): ReadonlyArray<number> {
    const {tokenIds} = this.props;
    return _(this.dragTokenIds)
      .map((id) => tokenIds.indexOf(id))
      .filter((index) => index >= 0)
      .value();
  }

  /*
   * API.
   */

  hasFocus() {
    const input = this.inputRef.current;
    return Boolean(input && input.hasFocus());
  }

  focus() {
    const input = this.inputRef.current;
    if (!input) {
      return;
    }

    input.focus();
  }

  moveToEnd() {
    const input = this.inputRef.current;
    if (!input) {
      return;
    }

    input.moveToEnd();
  }

  getAnchor(): Anchor {
    const input = this.inputRef.current;
    return input ? input.getAnchor() : fallbackInputAnchor;
  }

  getInputElement(): HTMLInputElement | undefined {
    return this.inputRef.current?.getElement() || undefined;
  }

  /*
   * Lifecycle.
   */

  componentWillUnmount() {
    globalWindow.clearTimeout(this.onBlurTimeout);
  }

  static getDerivedStateFromProps(props: Readonly<TokensEditorProps>, state: Readonly<TokensEditorState>) {
    if (state.selectedTokenIds.some((id) => !props.tokenIds.includes(id))) {
      return {
        selectedTokenIds: state.selectedTokenIds.filter((id) => props.tokenIds.includes(id)),
      };
    }

    return null;
  }

  /*
   * Wrapper event handlers.
   */

  private readonly onBlur = () => {
    globalWindow.clearTimeout(this.onBlurTimeout);
    this.onBlurTimeout = globalWindow.setInstrumentedTimeout(() => {
      const wrapper = this.wrapperRef.current;
      const input = this.inputRef.current;
      if (!wrapper || !input) {
        return;
      }

      // If the focus is still on a token inside, nothing to do.
      if (containsFocus(wrapper) && !input.hasFocus()) {
        return;
      }

      // Otherwise, clear the selection.
      this.setState({selectedTokenIds: []});
    }, 0);
  };

  /*
   * Token event handlers.
   */

  private readonly onTokenMouseDown = (e: FrontClickEvent, tokenId: string) => {
    // If the token is part of the current selection, drag the selection.
    const {selectedTokenIds} = this.state;
    if (selectedTokenIds.includes(tokenId)) {
      this.dragTokenIds = selectedTokenIds;
      return;
    }

    // If the Cmd key is down, drag the selection + the token.
    if (hasMetaKey(e)) {
      this.dragTokenIds = [...selectedTokenIds, tokenId];
      return;
    }

    // Otherwise, drag only this token.
    this.dragTokenIds = [tokenId];
  };

  private readonly onTokenClick = (e: FrontClickEvent, tokenId: string) => {
    this.setState(({selectedTokenIds}) => {
      // Find the ID to select.
      const {tokenIds} = this.props;
      const tokenIndexes = buildIndexMap(tokenIds);
      const newSelectedTokenIds = getSelectedIdsOnClick(selectedTokenIds, tokenIds, tokenIndexes, tokenId, e);

      // Update the state.
      return {selectedTokenIds: newSelectedTokenIds};
    });
  };

  private readonly onTokenDoubleClick = (_e: FrontClickEvent, tokenId: string) => {
    const {tokenIds, onEditTokenClick} = this.props;
    if (!onEditTokenClick) {
      return;
    }

    // Notify our parent.
    const tokenIndex = tokenIds.indexOf(tokenId);
    onEditTokenClick(tokenIndex);

    // Clear the selection.
    this.setState({selectedTokenIds: []});
  };

  private readonly onTokenArrowKeyDown: KeyboardEventHandler = (event) => {
    this.setState(({selectedTokenIds}) => {
      // If no token is selected, check if we need to select one.
      if (selectedTokenIds.length === 0) {
        return this.maybeSelectLastToken(event);
      }

      // Extract the properties we need from the props and state.
      const {tokenIds} = this.props;
      const tokenIndexes = buildIndexMap(tokenIds);

      // If only the last token is selected, we may need to select the input instead.
      const lastTokenId = last(tokenIds);
      const isUniqueSelectedToken = selectedTokenIds.length === 1;
      const isFirstSelectedLastToken = selectedTokenIds[0] === lastTokenId;
      const isOnlyLastTokenSelected = isUniqueSelectedToken && isFirstSelectedLastToken;

      const isKeyRightArrow = event.key === Key.ArrowRight && !event.shiftKey;
      if (isOnlyLastTokenSelected && isKeyRightArrow) {
        this.focus();
        return {selectedTokenIds: []};
      }

      // Otherwise, check if we need to update the selection.
      const newSelectedTokenIds = getSelectedIdsOnKey(
        selectedTokenIds,
        tokenIds,
        tokenIndexes,
        event,
        !isKeyRightArrow,
      );

      // Update the state.
      return {selectedTokenIds: newSelectedTokenIds};
    });
  };

  private readonly onTokenDeleteKeyDown: KeyboardEventHandler = (event) => {
    // If we're going to act, prevent other actions.
    if (this.state.selectedTokenIds.length > 0) {
      event.preventDefault();
    }

    if (this.state.selectedTokenIds.length === 0) {
      this.setState(this.maybeSelectLastToken(event));
    } else {
      const {tokenIds, onRemoveTokensClick} = this.props;
      const tokensToRemove = this.state.selectedTokenIds.map((id) => tokenIds.indexOf(id));
      onRemoveTokensClick(tokensToRemove);
      this.focus();
    }
  };

  private maybeSelectLastToken(event: FrontKeyboardEvent<HTMLElement>) {
    // Check if this is one of the keys we're looking for.
    const isLeftArrow = event.key === Key.ArrowLeft;
    const isBackspace = event.key === Key.Backspace;
    if (!isLeftArrow && !isBackspace) {
      return null;
    }

    const input = this.inputRef.current;
    if (!input) {
      return null;
    }

    const selection = input.getSelection();
    if (!selection) {
      return null;
    }

    // Check if we're at the very beginning of the input.
    const hasFocus = input.hasFocus();
    const isCursorAtBeginning = selection.start === 0 && selection.end === 0;

    const {tokenIds} = this.props;
    const lastTokenId = last(tokenIds);

    if (!hasFocus || !isCursorAtBeginning || !lastTokenId) {
      return null;
    }

    return {selectedTokenIds: [lastTokenId]};
  }

  /*
   * Input event handlers.
   */

  private readonly onInputChange = (newValue: string, keepSelection?: boolean) => {
    const input = this.inputRef.current;
    if (!input) {
      return;
    }

    this.props.onTextChange(newValue);

    // If needed, clear the token selection.
    if (keepSelection || !input.hasFocus()) {
      return;
    }

    this.setState({selectedTokenIds: []});
  };

  private readonly onInputPaste: ClipboardEventHandler = (event) => {
    const text = event.clipboardData && event.clipboardData.getData('text/plain');
    if (!text) {
      return;
    }

    const input = this.inputRef.current;
    if (!input) {
      return;
    }

    // Convert newline-separated tokens to comma-separated tokens.
    const inlinedText = text.trim().replace(/\s*[\r\n\f]+\s*/g, ', ');

    // Handle the paste, and add a trailing comma to encourage tokenization.
    input.replaceSelectedText(`${inlinedText}, `);
    event.preventDefault();

    // No change event is triggering onInputChange.
    this.props.onTextChange(input.getValue());
  };

  // Enter keys events are handled by dropdown only if there are items.
  // We need to handle these events if there are no items.
  private readonly onEnterKey = () => {
    const input = this.inputRef.current;
    if (!input) {
      return;
    }

    const value = input.getValue();
    // Add a trailing space to encourage tokenization.
    this.props.onTextChange(`${value} `);
  };

  private readonly onSelectAllKeyDown: KeyboardEventHandler = (event) => {
    const input = this.inputRef.current;
    if (!input) {
      return;
    }

    // If the input has focus and is not empty, use the browser default behavior.
    if (input.hasFocus() && input.getValue()) {
      return;
    }

    // Select all tokens.
    this.setState({selectedTokenIds: this.props.tokenIds});
    event.preventDefault();
  };

  private readonly onKeydown: KeyboardEventHandler = (event) => {
    switch (event.key) {
      case Key.Backspace:
      case Key.Delete:
        this.onTokenDeleteKeyDown(event);
        break;

      case Key.ArrowLeft:
      case Key.ArrowRight:
        this.onTokenArrowKeyDown(event);
        break;

      case Key.Enter:
        this.onEnterKey();
        break;

      case 'a':
        if (!hasMetaKey(event)) {
          break;
        }

        this.onSelectAllKeyDown(event);
        break;

      default:
        break;
    }
  };

  /*
   * Lifecycle.
   */

  componentDidUpdate(prevProps: Readonly<TokensEditorProps>, prevState: Readonly<TokensEditorState>) {
    // If tokens are selected and the selection changed, focus and select the hidden input.
    if (
      this.state.selectedTokenIds !== prevState.selectedTokenIds &&
      this.state.selectedTokenIds.length > 0
    ) {
      this.selectHiddenInput();
    }
  }

  private selectHiddenInput() {
    const hiddenInput = this.hiddenInputRef.current;
    if (!hiddenInput) {
      return;
    }

    // Keep focus in the TokensEditor, and select text for copying.
    hiddenInput.select();
  }

  /*
   * Render.
   */

  render() {
    return (
      <StyledWrapperDiv ref={this.wrapperRef} onBlur={this.onBlur} onKeyDown={this.onKeydown}>
        <StyledHiddenInput
          ref={this.hiddenInputRef}
          type="text"
          tabIndex={-1}
          value={this.renderTokensForCopy()}
          // Firefox doesn't let us copy from a readonly input, so use a noop change handler instead.
          onChange={noop}
        />
        {this.renderTokens()}
        {this.renderInput()}
      </StyledWrapperDiv>
    );
  }

  private renderTokensForCopy() {
    const {tokenIds, renderTokenForCopy} = this.props;
    const {selectedTokenIds} = this.state;

    if (!renderTokenForCopy) {
      return '';
    }

    return _(tokenIds)
      .map((id, index) => (selectedTokenIds.includes(id) ? renderTokenForCopy(index) : undefined))
      .compact()
      .join(', ');
  }

  private renderTokens() {
    const {tokenIds, renderToken} = this.props;
    const {selectedTokenIds} = this.state;

    return tokenIds.map((id, index) => {
      const isSelected = selectedTokenIds.includes(id);
      return (
        <ChromelessButtonWrapperDiv key={id}>
          <ChromelessButton
            ref={this.lastTokenRef}
            onMouseDown={(e) => this.onTokenMouseDown(e, id)}
            onClick={(e) => this.onTokenClick(e, id)}
            onDoubleClick={(e) => this.onTokenDoubleClick(e, id)}
          >
            {renderToken(index, isSelected)}
          </ChromelessButton>
        </ChromelessButtonWrapperDiv>
      );
    });
  }

  private renderInput() {
    const {shouldFocus, className, placeholder, tokenIds, text, onFocus, onBlur, inputId} = this.props;
    return (
      <StyledAutosizeInput
        widthStyleProp="flexBasis"
        ref={this.inputRef}
        className={className}
        type={InputTypesEnum.TEXT}
        placeholder={(tokenIds.length === 0 && placeholder) || undefined}
        value={text}
        onChange={this.onInputChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onPaste={this.onInputPaste}
        shouldFocus={shouldFocus}
        hasSpellCheck={false}
        inputId={inputId}
      />
    );
  }
}
